/**
 * The poke pop-ups (2026-10-29): a sticky stack at the window's top end,
 * newest first, each card on the toast glass (docs/design/visual-overhaul.md
 * §5.4: a layer that floats over moving content). A card stays until it is
 * closed: Reply opens the send-poke dialog to poke them back (shown only when
 * you could poke them, disabled with the reason while something blocks it),
 * Close closes it, and Close all appears from two cards up. Names and
 * messages are the sender's own text: `dir="auto"` in a <bdi>.
 *
 * On a phone or a tablet (§12.2) the stack keeps to the shown pane's free
 * band (`paneBand`): under its header, so Back and the header's actions stay
 * reachable, and above its composer, voice bar or dock, inside the safe
 * area (pokes.css); it scrolls inside when the cards need more (QA-01).
 */
import { useId, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n';
import { openModDialog } from '../mod/ModDialogs';
import { POKE_BLOCK_KEYS, pokeState } from '../mod/perms';
import { useData } from '../store/data';
import { useActiveGuildId } from '../store/selectors';
import { Avatar, Btn, Icon } from '../ui/kit';
import { followFrame, paneBand, portalRoot } from '../ui/overlay';
import { closeAllPokes, closePoke, senderName, usePokeStack, type StackPoke } from './stack';

/**
 * Off the desktop layout: the stack's top and height (`--pk-top`, `--pk-h`)
 * from the shown pane's band; the desktop keeps pokes.css's title-bar rule.
 * Returns what it measured, for `followFrame`.
 */
export function placePokeStack(el: HTMLElement): (Element | null)[] {
  const band = paneBand();
  if (!band) {
    el.style.removeProperty('--pk-top');
    el.style.removeProperty('--pk-h');
    return [];
  }
  // whole pixels; the stack's 8 of padding keeps the cards 8 inside the band
  const top = Math.ceil(band.top);
  el.style.setProperty('--pk-top', top + 'px');
  el.style.setProperty('--pk-h', Math.max(0, Math.floor(band.bottom) - top) + 'px');
  return band.measured;
}

export function PokeStack() {
  const { t } = useT();
  const pokes = usePokeStack((s) => s.pokes);
  const ref = useRef<HTMLElement>(null);
  const shown = pokes.length > 0;
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !shown) return;
    return followFrame(() => placePokeStack(el));
  }, [shown]);
  if (!shown) return null;
  return createPortal(
    <section className="pk-stack" aria-label={t('pk.stack')} ref={ref}>
      {pokes.length > 1 ? (
        <div className="pk-head nx-glass">
          <Icon name="zap" />
          <span className="pk-count nx-num">{t('pk.count', { n: pokes.length })}</span>
          <Btn size="xs" kind="ghost" className="pk-all" onClick={closeAllPokes}>
            {t('pk.closeAll')}
          </Btn>
        </div>
      ) : null}
      <ol className="pk-list">
        {pokes.map((p) => (
          <PokeCard key={p.key} poke={p} />
        ))}
      </ol>
    </section>,
    portalRoot(),
  );
}

function PokeCard({ poke }: { poke: StackPoke }) {
  const { t, fmt } = useT();
  const id = useId();
  const name = useData((d) => senderName(poke.from, d));
  const user = useData((d) => d.users[poke.from.id]) ?? poke.from;
  const gid = useActiveGuildId();
  // Reply as the member menu offers Poke: hidden when the rules refuse it,
  // disabled with the reason while you or they cannot right now
  const shown = useData((d) => !!gid && pokeState(d, gid, poke.from.id).shown);
  const blocked = useData((d) => (gid ? pokeState(d, gid, poke.from.id).blocked : null));
  const why = blocked ? t(POKE_BLOCK_KEYS[blocked]) : null;
  return (
    <li
      className="pk-card nx-glass"
      data-poke={poke.key}
      aria-labelledby={id + 'n'}
      aria-describedby={id + 'm'}
    >
      <div className="pk-top">
        <Avatar user={user} name={name} size={32} />
        <div className="pk-who">
          <bdi className="pk-name" dir="auto" id={id + 'n'}>
            {name}
          </bdi>
          <span className="pk-meta">
            {t('pk.got')} ·{' '}
            <time dateTime={new Date(poke.at).toISOString()}>{fmt.time(poke.at)}</time>
          </span>
        </div>
        <span className="pk-ic" aria-hidden="true">
          <Icon name="zap" />
        </span>
      </div>
      {poke.message ? (
        <p className="pk-msg" dir="auto" id={id + 'm'}>
          <bdi>{poke.message}</bdi>
        </p>
      ) : (
        <p className="pk-msg empty" id={id + 'm'}>
          {t('pk.noMessage')}
        </p>
      )}
      {why ? (
        <p className="pk-why" id={id + 'w'}>
          {why}
        </p>
      ) : null}
      <div className="pk-acts">
        <Btn size="xs" kind="ghost" onClick={() => closePoke(poke.key)}>
          {t('pk.close')}
        </Btn>
        {shown && gid ? (
          <Btn
            size="xs"
            kind="primary"
            icon="reply"
            disabled={!!blocked}
            aria-describedby={why ? id + 'w' : undefined}
            onClick={() => openModDialog({ kind: 'poke', guildId: gid, userId: poke.from.id })}
          >
            {t('pk.reply')}
          </Btn>
        ) : null}
      </div>
    </li>
  );
}
